class ProductInfoTabs extends HTMLElement {
  constructor() {
    super();
    this.tabButtons = this.querySelectorAll(".product-info-tabs__tab");
    this.tabPanels = this.querySelectorAll(".product-info-tabs__panel");

    this.init();
  }

  init() {
    // Add click event listeners to tab buttons
    this.tabButtons.forEach((button, index) => {
      button.addEventListener("click", () => this.switchTab(index));
      button.addEventListener("keydown", (e) => this.handleKeyDown(e, index));
    });

    // Set initial ARIA attributes
    this.setInitialAttributes();
  }

  setInitialAttributes() {
    this.tabButtons.forEach((button, index) => {
      button.setAttribute("tabindex", index === 0 ? "0" : "-1");
    });
  }

  switchTab(targetIndex) {
    // Update tab buttons
    this.tabButtons.forEach((button, index) => {
      const isActive = index === targetIndex;

      button.classList.toggle("product-info-tabs__tab--active", isActive);
      button.setAttribute("aria-selected", isActive.toString());
      button.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    // Update tab panels
    this.tabPanels.forEach((panel, index) => {
      const isActive = index === targetIndex;

      panel.classList.toggle("product-info-tabs__panel--active", isActive);
      panel.toggleAttribute("hidden", !isActive);

      if (isActive) {
        panel.removeAttribute("hidden");
      } else {
        panel.setAttribute("hidden", "");
      }

      if (panel.content) {
        panel.content.classList.toggle("product-info-tabs__app--active", isActive);
      }

      // Check if this is a reviews panel and trigger resize
      if (isActive) {
        const reviewsWidget = panel.querySelector(".jdgm-rev-widg__reviews");
        if (reviewsWidget) {
          // Force reviews widget to recalculate height
          this.handleReviewsResize(reviewsWidget);
        }
      }
    });

    // Announce the change to screen readers
    this.announceTabChange(targetIndex);
  }

  handleKeyDown(event, currentIndex) {
    let targetIndex = currentIndex;

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        targetIndex = (currentIndex + 1) % this.tabButtons.length;
        break;
      case "ArrowLeft":
        event.preventDefault();
        targetIndex =
          currentIndex === 0 ? this.tabButtons.length - 1 : currentIndex - 1;
        break;
      case "Home":
        event.preventDefault();
        targetIndex = 0;
        break;
      case "End":
        event.preventDefault();
        targetIndex = this.tabButtons.length - 1;
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        this.switchTab(currentIndex);
        return;
      default:
        return;
    }

    // Switch to the target tab and focus it
    this.switchTab(targetIndex);
    this.tabButtons[targetIndex].focus();
  }

  handleReviewsResize(reviewsWidget) {
    // Force Judge.me widget to recalculate height
    const triggerResize = () => {
      window.dispatchEvent(new Event("resize"));
      
      // Also trigger scroll event which some widgets listen to
      window.dispatchEvent(new Event("scroll"));
      
      // Check if Judge.me widget has a specific method to refresh
      if (window.jdgm && typeof window.jdgm.refresh === "function") {
        window.jdgm.refresh();
      }
    };

    // Trigger resize multiple times with increasing delays
    // This ensures the widget has time to initialize and render
    setTimeout(triggerResize, 50);
    setTimeout(triggerResize, 150);
    setTimeout(triggerResize, 300);
    
    // Final check: if height is still 0, force visibility recalculation
    setTimeout(() => {
      const widgetBody = reviewsWidget.querySelector(".jdgm-rev-widg__body");
      if (widgetBody) {
        const height = widgetBody.offsetHeight;
        if (height === 0) {
          // Force reflow by temporarily hiding and showing
          widgetBody.style.display = "none";
          widgetBody.offsetHeight; // Trigger reflow
          widgetBody.style.display = "";
          
          // Trigger resize one more time
          setTimeout(triggerResize, 100);
        }
      }
    }, 500);
  }

  announceTabChange(tabIndex) {
    const tabButton = this.tabButtons[tabIndex];
    const tabText = tabButton.textContent.trim();

    // Create a temporary element to announce the change
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("aria-atomic", "true");
    announcement.style.position = "absolute";
    announcement.style.left = "-10000px";
    announcement.style.width = "1px";
    announcement.style.height = "1px";
    announcement.style.overflow = "hidden";

    document.body.appendChild(announcement);

    // Announce the change
    announcement.textContent = `${tabText} tab selected`;

    // Clean up after a short delay
    setTimeout(() => {
      if (announcement.parentNode) {
        announcement.parentNode.removeChild(announcement);
      }
    }, 1000);
  }
}

customElements.define("product-info-tabs", ProductInfoTabs);
